import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import axiosClient from "../api/axiosClient";
import { useWebSocket } from "../contexts/WebSocketContext";

// Dedupe a nivel de módulo: hay varias instancias del hook montadas a la vez
// (Sidebar, MobileBottomNav, páginas). Comparten un único fetch en vuelo y un
// único debounce de eventos WS; el resultado llega a todas vía SYNC_EVENT.
let unreadChatsInFlight = null;
let wsChatsDebounceTimer = null;

export default function useGlobalNotifications() {
  // Canal simple de sincronización entre múltiples instancias del hook
  const SYNC_EVENT = "conextcrm:notifications_sync";
  const [counters, setCounters] = useState({
    unread_messages: 0,
    new_contacts: 0,
    new_documents: 0,
    new_documents_by_contact: {},
    // Notificaciones específicas de chats
    chat_notifications: {
      whatsapp: 0,
      total: 0,
    },
  });
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const { addMessageHandler, removeMessageHandler, hasToken, isConnected } = useWebSocket();
  const loadCountersRef = useRef();
  const loadUnreadChatsRef = useRef();

  // Cargar contadores iniciales (new_contacts, new_documents — NO unread_messages)
  const loadCounters = useCallback(async () => {
    try {
      // console.log("📊 Loading notification counters...");
      setLoading(true);
      const response = await axiosClient.get("/notification-counters");
      const perContactRaw =
        response?.new_documents_by_contact &&
        typeof response.new_documents_by_contact === "object"
          ? response.new_documents_by_contact
          : {};
      const perContact = Object.keys(perContactRaw).reduce((acc, key) => {
        acc[key] = Number(perContactRaw[key]) || 0;
        return acc;
      }, {});

      const patch = {
        new_contacts: Number(response?.new_contacts) || 0,
        new_documents: Number(response?.new_documents) || 0,
        new_documents_by_contact: perContact,
      };
      // console.log("✅ Counters loaded:", patch);
      setCounters((prev) => ({ ...prev, ...patch }));
      // Sincronizar con otros consumidores (Sidebar, etc.)
      try {
        window.dispatchEvent(
          new CustomEvent(SYNC_EVENT, { detail: { patch } })
        );
      } catch (_) {}
    } catch (error) {
      console.error("❌ Error loading notification counters:", error);
      // En caso de error, preservar estado previo, solo resetear los 3 campos propios
      setCounters((prev) => ({
        ...prev,
        new_contacts: 0,
        new_documents: 0,
        new_documents_by_contact: {},
      }));
    } finally {
      setLoading(false);
    }
  }, []);

  // Guardar referencia a loadCounters
  loadCountersRef.current = loadCounters;

  // Cargar contador de chats no leídos desde el nuevo endpoint source-of-truth.
  // Dedupe: si ya hay un fetch en vuelo (de esta u otra instancia), se reutiliza;
  // el resto de instancias se actualizan por el SYNC_EVENT que despacha el fetcher.
  const loadUnreadChats = useCallback(() => {
    if (unreadChatsInFlight) return unreadChatsInFlight;
    unreadChatsInFlight = (async () => {
      try {
        const r = await axiosClient.get("/chat-unread-count");
        const chatCounts = {
          whatsapp: Number(r?.whatsapp) || 0,
          total: Number(r?.total) || 0,
        };
        setCounters((prev) => ({
          ...prev,
          chat_notifications: chatCounts,
          unread_messages: chatCounts.total,
        }));
        // Sincronizar con otras instancias
        try {
          window.dispatchEvent(
            new CustomEvent(SYNC_EVENT, {
              detail: {
                patch: {
                  chat_notifications: chatCounts,
                  unread_messages: chatCounts.total,
                },
              },
            })
          );
        } catch (_) {}
      } catch (error) {
        console.error("❌ Error loading unread chat count:", error);
        // NO resetear — preservar valor previo
      } finally {
        unreadChatsInFlight = null;
      }
    })();
    return unreadChatsInFlight;
  }, []);

  // Guardar referencia a loadUnreadChats
  loadUnreadChatsRef.current = loadUnreadChats;

  // Actualizar contadores de chats (llamado por el evento local de Chats.jsx)
  // Ya NO deriva unread_messages — solo actualiza chat_notifications para UI interna
  const updateChatNotifications = useCallback((chatCounts) => {
    // console.log("[GLOBAL_NOTIFICATIONS] Updating chat notifications:", chatCounts);
    setCounters((prev) => ({
      ...prev,
      chat_notifications: chatCounts,
    }));
  }, []);

  // Resetear contador cuando se entra en una sección (ACTIVO)
  const resetCounter = useCallback(async (type) => {
    // Guard: unread_messages ya no se resetea vía POST — se recarga desde el backend
    if (type === "unread_messages") {
      return loadUnreadChatsRef.current && loadUnreadChatsRef.current();
    }
    try {
      await axiosClient.post("/notification-counters/reset", {
        notification_type: type,
      });
      setCounters((prev) => {
        if (type === "new_documents") {
          return { ...prev, new_documents: 0 };
        }
        if (type && type.startsWith("new_documents#CONTACT#")) {
          const contactId = type.split("#CONTACT#")[1];
          if (!contactId) return prev;
          const nextMap = {
            ...(prev.new_documents_by_contact || {}),
            [contactId]: 0,
          };
          return { ...prev, new_documents_by_contact: nextMap };
        }
        return { ...prev, [type]: 0 };
      });
      // Notificar a otras instancias para que reflejen el reset al instante
      try {
        window.dispatchEvent(
          new CustomEvent(SYNC_EVENT, { detail: { patch: { [type]: 0 } } })
        );
      } catch (_) {}
    } catch (error) {
      console.error("❌ Error resetting counter:", error);
    }
  }, []);

  // Handler para mensajes WebSocket
  const handleWebSocketMessage = useCallback(
    (data) => {
      if (data.action === "new_message" || data.action === "chat_read") {
        // Debounce ~300ms compartido entre instancias para agrupar ráfagas
        if (wsChatsDebounceTimer) clearTimeout(wsChatsDebounceTimer);
        wsChatsDebounceTimer = setTimeout(() => {
          wsChatsDebounceTimer = null;
          if (loadUnreadChatsRef.current) {
            loadUnreadChatsRef.current();
          }
        }, 300);
      } else if (data.action === "new_contact" || data.action === "new_document") {
        // Usar la referencia para evitar dependencias
        if (loadCountersRef.current) {
          loadCountersRef.current();
        }
      }
    },
    [] // Sin dependencias para evitar bucles
  );

  // Registrar handler de WebSocket
  useEffect(() => {
    addMessageHandler(handleWebSocketMessage);

    return () => {
      removeMessageHandler(handleWebSocketMessage);
    };
  }, [addMessageHandler, handleWebSocketMessage, removeMessageHandler]);

  // Escuchar sincronizaciones de otras instancias del hook
  useEffect(() => {
    const onSync = (ev) => {
      const detail = ev?.detail || {};
      if (detail.patch) {
        setCounters((prev) => ({ ...prev, ...detail.patch }));
      } else if (detail.counters) {
        // Retrocompatibilidad: soporte legado
        setCounters((prev) => ({ ...prev, ...detail.counters }));
      }
    };
    window.addEventListener(SYNC_EVENT, onSync);
    return () => window.removeEventListener(SYNC_EVENT, onSync);
  }, []);

  // Escuchar actualizaciones de notificaciones de chats (evento local de Chats.jsx)
  useEffect(() => {
    const handler = (ev) => {
      if (ev?.detail?.counts) {
        updateChatNotifications(ev.detail.counts);
      }
    };
    window.addEventListener("conextcrm:chat_notifications_update", handler);
    return () => window.removeEventListener("conextcrm:chat_notifications_update", handler);
  }, [updateChatNotifications]);

  // Cargar contadores al montar el componente
  useEffect(() => {
    if (!hasToken) return;
    let canceled = false;
    const run = async () => {
      try {
        await loadCounters();
      } catch {}
    };
    const runChats = async () => {
      try {
        await loadUnreadChats();
      } catch {}
    };
    // Debounce initial load slightly to avoid bursts on route changes
    const t = setTimeout(() => {
      if (!canceled) {
        run();
        runChats();
      }
    }, 150);
    return () => {
      canceled = true;
      clearTimeout(t);
    };
  }, [hasToken, loadCounters, loadUnreadChats]);

  // Polling fallback: recargar chats no leídos cada 60s si el WS no está conectado
  useEffect(() => {
    if (!hasToken) return;
    const id = setInterval(() => {
      if (!isConnected && document.visibilityState === "visible") {
        if (loadUnreadChatsRef.current) {
          loadUnreadChatsRef.current();
        }
      }
    }, 60000);
    return () => clearInterval(id);
  }, [hasToken, isConnected]);

  // Resetear contadores automáticamente al cambiar de ruta (DESACTIVADO)
  useEffect(() => {
    // location.pathname - counter reset disabled
  }, [location.pathname]);

  return {
    counters,
    loading,
    resetCounter,
    loadCounters,
    loadUnreadChats,
    updateChatNotifications,
  };
}
